import React, { useEffect, useState } from 'react';
import { Dropdown, Form, Grid, Header, Input, Message, Modal } from 'semantic-ui-react';

import Button from '../../../components/Button';
import { AddressService } from '../../../services/address';
import { getUsers } from '../../../services/estate';
import {
  maskCpf,
  maskPhoneNumber,
  maskZipCode,
  unmaskCpfOrCnpj,
  unmaskPhoneNumber
} from '../../../services/masks';
import { dangerNotification } from '../../../services/notification';
import { UserState } from '../../../types/UserState';
import { isValidEmail } from '../../../utils/emailValidator';

import { initialCreateCommissioningCollaboratorForm } from '../constants';
import {
  CommissioningCollaborator,
  CreateCommissioningCollaboratorFormErrors,
  CreateCommissioningCollaboratorFormState
} from '../types';

type Props = {
  open: boolean;
  collaborator: CommissioningCollaborator | null;
  isBrokerAdmin: boolean;
  isEstateAdmin: boolean;
  estateOptions: Array<{ key: string; text: string; value: string }>;
  saving: boolean;
  onClose: () => void;
  onSubmit: (form: CreateCommissioningCollaboratorFormState) => void;
};

type DropdownOption = {
  key: string | number;
  text: string;
  value: string;
};

const requiredFields: Array<keyof CreateCommissioningCollaboratorFormState> = [
  'name',
  'email',
  'cpf',
  'birthdate',
  'monthlyIncome',
  'professionalOccupation',
  'phone',
  'street',
  'streetNumber',
  'neighborhood',
  'city',
  'state',
  'zipCode',
  'bankCode',
  'bankBranchNumber',
  'bankAccountNumber',
  'bankAccountCheckDigit'
];

const getUserDocument = (user: UserState) =>
  String(user.document || (user as UserState & { doc?: string }).doc || '');

const getUserPhone = (user: UserState) =>
  String(user.cellphone || user.phone || '');

const toUserOptions = (users: UserState[]): DropdownOption[] =>
  users.map((user, index) => ({
    key: user.id || index,
    text: user.name || user.email || 'Usuário sem nome',
    value: user.id
  }));

const ensureCurrentUserOption = (
  options: DropdownOption[],
  collaborator: CommissioningCollaborator | null
): DropdownOption[] => {
  if (!collaborator?.userId || options.some(option => option.value === collaborator.userId)) {
    return options;
  }

  return [
    {
      key: collaborator.userId,
      text: collaborator.name || 'Usuário selecionado',
      value: collaborator.userId
    },
    ...options
  ];
};

const validateForm = (
  form: CreateCommissioningCollaboratorFormState
): CreateCommissioningCollaboratorFormErrors => {
  const errors: CreateCommissioningCollaboratorFormErrors = {};

  requiredFields.forEach(field => {
    const value = form[field];
    if (!String(value || '').trim()) {
      errors[field] = 'Campo obrigatório.';
    }
  });

  if (form.email && !isValidEmail(form.email)) {
    errors.email = 'Informe um e-mail válido.';
  }

  if (unmaskCpfOrCnpj(form.cpf).length !== 11) {
    errors.cpf = 'Informe um CPF válido.';
  }

  if (form.phone && unmaskPhoneNumber(form.phone).length < 10) {
    errors.phone = 'Informe um celular válido.';
  }

  if (form.bankCode && form.bankCode.length > 3) {
    errors.bankCode = 'Selecione um banco válido.';
  }

  if (form.zipCode.replace(/\D/g, '').length !== 8) {
    errors.zipCode = 'Informe um CEP válido.';
  }

  if (form.state.trim().length !== 2) {
    errors.state = 'Informe a UF com 2 letras.';
  }

  if (Number(form.monthlyIncome) <= 0) {
    errors.monthlyIncome = 'Informe uma renda válida.';
  }

  return errors;
};

const getActivationValue = (payload: any, path: string, fallback = '') => {
  const value = path.split('.').reduce((current, key) => current?.[key], payload);
  return value === undefined || value === null ? fallback : String(value);
};

const toInputBirthdate = (value: string) => {
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(value)) {
    const [dd, mm, yyyy] = value.split('/');
    return `${yyyy}-${mm}-${dd}`;
  }
  return value;
};

const getInitialForm = (
  collaborator: CommissioningCollaborator | null
): CreateCommissioningCollaboratorFormState => {
  const payload = (collaborator?.activation?.payload || {}) as any;
  const registerInformation = payload.registerInformation || payload.register_information || {};
  const defaultBankAccount = payload.defaultBankAccount || payload.default_bank_account || {};
  const address = registerInformation.address || {};

  return {
    ...initialCreateCommissioningCollaboratorForm,
    estateId: collaborator?.estateId || '',
    userId: collaborator?.userId || '',
    name: collaborator?.name === '-' ? '' : collaborator?.name || '',
    email: collaborator?.email || '',
    cpf: collaborator?.cpf === '-' ? '' : collaborator?.cpf || '',
    phone: collaborator?.phone === '-' ? '' : collaborator?.phone || '',
    birthdate: toInputBirthdate(getActivationValue(registerInformation, 'birthdate')),
    monthlyIncome: getActivationValue(registerInformation, 'monthly_income'),
    professionalOccupation: getActivationValue(registerInformation, 'professional_occupation'),
    street: getActivationValue(address, 'street'),
    streetNumber: getActivationValue(address, 'street_number'),
    neighborhood: getActivationValue(address, 'neighborhood'),
    city: getActivationValue(address, 'city'),
    state: getActivationValue(address, 'state'),
    zipCode: maskZipCode(getActivationValue(address, 'zip_code')),
    referencePoint: getActivationValue(address, 'reference_point'),
    complementary: getActivationValue(address, 'complementary'),
    bankCode: getActivationValue(defaultBankAccount, 'bank'),
    bankBranchNumber: getActivationValue(defaultBankAccount, 'branch_number'),
    bankBranchCheckDigit: getActivationValue(defaultBankAccount, 'branch_check_digit'),
    bankAccountNumber: getActivationValue(defaultBankAccount, 'account_number'),
    bankAccountCheckDigit: getActivationValue(defaultBankAccount, 'account_check_digit'),
    bankAccountType:
      getActivationValue(defaultBankAccount, 'type') === 'savings' ? 'savings' : 'checking'
  };
};

const EditCollaboratorModal: React.FC<Props> = ({
  open,
  collaborator,
  isBrokerAdmin,
  isEstateAdmin,
  estateOptions,
  saving,
  onClose,
  onSubmit
}) => {
  const [form, setForm] = useState<CreateCommissioningCollaboratorFormState>(
    initialCreateCommissioningCollaboratorForm
  );
  const [errors, setErrors] = useState<CreateCommissioningCollaboratorFormErrors>({});
  const [users, setUsers] = useState<UserState[]>([]);
  const [userOptions, setUserOptions] = useState<DropdownOption[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [addressLookupLoading, setAddressLookupLoading] = useState(false);
  const [addressLookupError, setAddressLookupError] = useState<string | null>(null);

  const canShowEstate = isBrokerAdmin;
  const selectedEstateId = form.estateId || collaborator?.estateId || '';

  useEffect(() => {
    if (!open) return;
    setForm(getInitialForm(collaborator));
    setErrors({});
    setUsers([]);
    setUserOptions([]);
    setAddressLookupError(null);
  }, [collaborator, open]);

  useEffect(() => {
    if (!open || !selectedEstateId || (!isBrokerAdmin && !isEstateAdmin)) return;

    const loadUsers = async () => {
      setUsersLoading(true);
      try {
        const usersResponse = await getUsers(selectedEstateId);
        const loadedUsers = usersResponse || [];
        setUsers(loadedUsers);
        setUserOptions(ensureCurrentUserOption(toUserOptions(loadedUsers), collaborator));

        const currentUser = loadedUsers.find(user => user.id === form.userId);
        if (currentUser) {
          setForm(prev => ({
            ...prev,
            name: currentUser.name || prev.name,
            email: currentUser.email || prev.email,
            cpf: maskCpf(getUserDocument(currentUser)) || prev.cpf,
            phone: maskPhoneNumber(getUserPhone(currentUser)) || prev.phone
          }));
        }
      } catch (error) {
        setUsers([]);
        setUserOptions([]);
        dangerNotification('Erro', 'Não foi possível carregar usuários da imobiliária.');
      } finally {
        setUsersLoading(false);
      }
    };

    loadUsers();
  }, [collaborator, form.userId, isBrokerAdmin, isEstateAdmin, open, selectedEstateId]);

  const updateField = (
    field: keyof CreateCommissioningCollaboratorFormState,
    value: string
  ) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: undefined }));
  };

  const applySelectedUser = (userId: string) => {
    const selectedUser = users.find(user => user.id === userId);

    setForm(prev => ({
      ...prev,
      userId,
      name: selectedUser?.name || prev.name,
      email: selectedUser?.email || prev.email,
      cpf: selectedUser ? maskCpf(getUserDocument(selectedUser)) : prev.cpf,
      phone: selectedUser ? maskPhoneNumber(getUserPhone(selectedUser)) : prev.phone
    }));
    setErrors(prev => ({ ...prev, userId: undefined }));
  };

  const handleZipCodeLookup = async (value: string) => {
    const masked = maskZipCode(value);
    updateField('zipCode', masked);
    const cleanZip = masked.replace(/\D/g, '');

    if (cleanZip.length !== 8) {
      setAddressLookupError(null);
      return;
    }

    setAddressLookupLoading(true);
    setAddressLookupError(null);
    try {
      const response = await AddressService.getAddressByZipCode(cleanZip);
      setForm(prev => ({
        ...prev,
        zipCode: masked,
        street: response.street || prev.street,
        neighborhood: response.district || prev.neighborhood,
        city: response.city || prev.city,
        state: response.state ? String(response.state).toUpperCase() : prev.state
      }));
    } catch (error) {
      setAddressLookupError('CEP não encontrado. Verifique e tente novamente.');
    } finally {
      setAddressLookupLoading(false);
    }
  };

  const handleSubmit = () => {
    const nextErrors = validateForm(form);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) {
      dangerNotification(
        'Validação',
        'Preencha corretamente os campos obrigatórios do colaborador.',
        6000
      );
      return;
    }

    onSubmit(form);
  };

  return (
    <Modal open={open} onClose={onClose} size="large" closeIcon>
      <Modal.Header>Editar colaborador</Modal.Header>
      <Modal.Content scrolling>
        <Form>
          <Grid>
            <Grid.Row>
              {canShowEstate && (
                <Grid.Column computer={8} tablet={16} mobile={16}>
                  <Form.Field>
                    <label>Imobiliária</label>
                    <Dropdown
                      fluid
                      search
                      selection
                      disabled
                      placeholder="Selecionar..."
                      value={form.estateId}
                      options={estateOptions}
                    />
                  </Form.Field>
                </Grid.Column>
              )}

              <Grid.Column computer={canShowEstate ? 8 : 16} tablet={16} mobile={16}>
                <Form.Field error={Boolean(errors.userId)}>
                  <label>Usuário</label>
                  <Dropdown
                    fluid
                    search
                    selection
                    clearable
                    placeholder="Selecionar..."
                    value={form.userId}
                    options={userOptions}
                    loading={usersLoading}
                    disabled={!selectedEstateId || usersLoading}
                    onChange={(_, { value }) => applySelectedUser(String(value || ''))}
                  />
                  {errors.userId && <span style={{ color: '#9f3a38', fontSize: 12 }}>{errors.userId}</span>}
                </Form.Field>
              </Grid.Column>
            </Grid.Row>

            <Grid.Row columns={1}>
              <Grid.Column>
                <Header as="h4">Dados do recebedor</Header>
              </Grid.Column>
            </Grid.Row>

            <Grid.Row columns={4}>
              <Grid.Column computer={6} tablet={16} mobile={16}>
                <Form.Field error={Boolean(errors.name)}>
                  <label>Nome completo *</label>
                  <Input value={form.name} disabled={Boolean(form.userId)} onChange={(_, { value }) => updateField('name', value)} />
                  {errors.name && <span style={{ color: '#9f3a38', fontSize: 12 }}>{errors.name}</span>}
                </Form.Field>
              </Grid.Column>
              <Grid.Column computer={4} tablet={8} mobile={16}>
                <Form.Field error={Boolean(errors.cpf)}>
                  <label>CPF *</label>
                  <Input value={form.cpf} onChange={(_, { value }) => updateField('cpf', maskCpf(value))} />
                  {errors.cpf && <span style={{ color: '#9f3a38', fontSize: 12 }}>{errors.cpf}</span>}
                </Form.Field>
              </Grid.Column>
              <Grid.Column computer={6} tablet={8} mobile={16}>
                <Form.Field error={Boolean(errors.email)}>
                  <label>E-mail *</label>
                  <Input value={form.email} disabled={Boolean(form.userId)} onChange={(_, { value }) => updateField('email', value)} />
                  {errors.email && <span style={{ color: '#9f3a38', fontSize: 12 }}>{errors.email}</span>}
                </Form.Field>
              </Grid.Column>
            </Grid.Row>

            <Grid.Row columns={4}>
              <Grid.Column computer={4} tablet={8} mobile={16}>
                <Form.Field>
                  <label>Celular</label>
                  <Input
                    placeholder="(00) 00000-0000"
                    value={form.phone}
                    onChange={(_, { value }) => updateField('phone', maskPhoneNumber(value))}
                  />
                </Form.Field>
              </Grid.Column>
              <Grid.Column computer={4} tablet={8} mobile={16}>
                <Form.Field error={Boolean(errors.birthdate)}>
                  <label>Data de nascimento *</label>
                  <Input type="date" value={form.birthdate} onChange={(_, { value }) => updateField('birthdate', value)} />
                  {errors.birthdate && <span style={{ color: '#9f3a38', fontSize: 12 }}>{errors.birthdate}</span>}
                </Form.Field>
              </Grid.Column>
              <Grid.Column computer={4} tablet={8} mobile={16}>
                <Form.Field error={Boolean(errors.monthlyIncome)}>
                  <label>Renda mensal *</label>
                  <Input
                    type="number"
                    min="0"
                    value={form.monthlyIncome}
                    onChange={(_, { value }) => updateField('monthlyIncome', value)}
                  />
                  {errors.monthlyIncome && <span style={{ color: '#9f3a38', fontSize: 12 }}>{errors.monthlyIncome}</span>}
                </Form.Field>
              </Grid.Column>
              <Grid.Column computer={4} tablet={8} mobile={16}>
                <Form.Field error={Boolean(errors.professionalOccupation)}>
                  <label>Profissão *</label>
                  <Input value={form.professionalOccupation} onChange={(_, { value }) => updateField('professionalOccupation', value)} />
                  {errors.professionalOccupation && (
                    <span style={{ color: '#9f3a38', fontSize: 12 }}>{errors.professionalOccupation}</span>
                  )}
                </Form.Field>
              </Grid.Column>
            </Grid.Row>

            <Grid.Row columns={1}>
              <Grid.Column>
                <Header as="h4">Endereço</Header>
                {addressLookupError && <Message negative size="small">{addressLookupError}</Message>}
              </Grid.Column>
            </Grid.Row>

            <Grid.Row columns={4}>
              <Grid.Column computer={4} tablet={8} mobile={16}>
                <Form.Field error={Boolean(errors.zipCode)}>
                  <label>CEP *</label>
                  <Input value={form.zipCode} loading={addressLookupLoading} onChange={(_, { value }) => handleZipCodeLookup(value)} />
                  {errors.zipCode && <span style={{ color: '#9f3a38', fontSize: 12 }}>{errors.zipCode}</span>}
                </Form.Field>
              </Grid.Column>
              <Grid.Column computer={6} tablet={8} mobile={16}>
                <Form.Field error={Boolean(errors.street)}>
                  <label>Rua *</label>
                  <Input value={form.street} onChange={(_, { value }) => updateField('street', value)} />
                  {errors.street && <span style={{ color: '#9f3a38', fontSize: 12 }}>{errors.street}</span>}
                </Form.Field>
              </Grid.Column>
              <Grid.Column computer={3} tablet={8} mobile={16}>
                <Form.Field error={Boolean(errors.streetNumber)}>
                  <label>Número *</label>
                  <Input value={form.streetNumber} onChange={(_, { value }) => updateField('streetNumber', value)} />
                  {errors.streetNumber && <span style={{ color: '#9f3a38', fontSize: 12 }}>{errors.streetNumber}</span>}
                </Form.Field>
              </Grid.Column>
              <Grid.Column computer={3} tablet={8} mobile={16}>
                <Form.Field error={Boolean(errors.state)}>
                  <label>UF *</label>
                  <Input maxLength={2} value={form.state} onChange={(_, { value }) => updateField('state', value.toUpperCase())} />
                  {errors.state && <span style={{ color: '#9f3a38', fontSize: 12 }}>{errors.state}</span>}
                </Form.Field>
              </Grid.Column>
            </Grid.Row>

            <Grid.Row columns={4}>
              <Grid.Column computer={5} tablet={8} mobile={16}>
                <Form.Field error={Boolean(errors.neighborhood)}>
                  <label>Bairro *</label>
                  <Input value={form.neighborhood} onChange={(_, { value }) => updateField('neighborhood', value)} />
                  {errors.neighborhood && <span style={{ color: '#9f3a38', fontSize: 12 }}>{errors.neighborhood}</span>}
                </Form.Field>
              </Grid.Column>
              <Grid.Column computer={5} tablet={8} mobile={16}>
                <Form.Field error={Boolean(errors.city)}>
                  <label>Cidade *</label>
                  <Input value={form.city} onChange={(_, { value }) => updateField('city', value)} />
                  {errors.city && <span style={{ color: '#9f3a38', fontSize: 12 }}>{errors.city}</span>}
                </Form.Field>
              </Grid.Column>
              <Grid.Column computer={3} tablet={8} mobile={16}>
                <Form.Field>
                  <label>Complemento</label>
                  <Input value={form.complementary} onChange={(_, { value }) => updateField('complementary', value)} />
                </Form.Field>
              </Grid.Column>
              <Grid.Column computer={3} tablet={8} mobile={16}>
                <Form.Field error={Boolean(errors.referencePoint)}>
                  <label>Referência</label>
                  <Input value={form.referencePoint} onChange={(_, { value }) => updateField('referencePoint', value)} />
                  {errors.referencePoint && <span style={{ color: '#9f3a38', fontSize: 12 }}>{errors.referencePoint}</span>}
                </Form.Field>
              </Grid.Column>
            </Grid.Row>

            <Grid.Row columns={1}>
              <Grid.Column>
                <Header as="h4">Dados bancários</Header>
              </Grid.Column>
            </Grid.Row>

            <Grid.Row columns={4}>
              <Grid.Column computer={3} tablet={8} mobile={16}>
                <Form.Field error={Boolean(errors.bankCode)}>
                  <label>Banco *</label>
                  <Input value={form.bankCode} onChange={(_, { value }) => updateField('bankCode', value.replace(/\D/g, ''))} />
                  {errors.bankCode && <span style={{ color: '#9f3a38', fontSize: 12 }}>{errors.bankCode}</span>}
                </Form.Field>
              </Grid.Column>
              <Grid.Column computer={4} tablet={8} mobile={16}>
                <Form.Field error={Boolean(errors.bankBranchNumber)}>
                  <label>Agência *</label>
                  <Input
                    value={form.bankBranchNumber}
                    onChange={(_, { value }) => updateField('bankBranchNumber', value.replace(/\D/g, ''))}
                  />
                  {errors.bankBranchNumber && <span style={{ color: '#9f3a38', fontSize: 12 }}>{errors.bankBranchNumber}</span>}
                </Form.Field>
              </Grid.Column>
              <Grid.Column computer={3} tablet={8} mobile={16}>
                <Form.Field>
                  <label>Dígito agência</label>
                  <Input value={form.bankBranchCheckDigit} onChange={(_, { value }) => updateField('bankBranchCheckDigit', value)} />
                </Form.Field>
              </Grid.Column>
              <Grid.Column computer={6} tablet={8} mobile={16}>
                <Form.Field>
                  <label>Tipo de conta *</label>
                  <Dropdown
                    fluid
                    selection
                    value={form.bankAccountType}
                    options={[
                      { key: 'checking', text: 'Conta corrente', value: 'checking' },
                      { key: 'savings', text: 'Conta poupança', value: 'savings' }
                    ]}
                    onChange={(_, { value }) =>
                      updateField(
                        'bankAccountType',
                        value as CreateCommissioningCollaboratorFormState['bankAccountType']
                      )
                    }
                  />
                </Form.Field>
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </Form>
      </Modal.Content>
      <Modal.Actions>
        <Button type="button" text="Cancelar" light onClick={onClose} disabled={saving} />
        <Button type="button" text="Confirmar" loading={saving} disabled={saving} onClick={handleSubmit} />
      </Modal.Actions>
    </Modal>
  );
};

export default EditCollaboratorModal;
